/* eslint-disable react-hooks/exhaustive-deps */
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import utils from 'utils';
import uniqBy from 'lodash/uniqBy';
import { PaginatedData } from 'models/PaginatedData';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DEBOUNCE_TIME } from 'data/global';

type InfiniteProps<T> = {
  makeRequest: (
    currentPage: number,
    searchString: string,
  ) => Promise<PaginatedData<T>>;
  customSetItems?: React.Dispatch<React.SetStateAction<T[]>>;
  mappingFunction?: (items: Array<T>) => Array<any>;
  onItemsLoad?: (currentPage: number, items: Array<T>) => void;
  onResetDeps?: () => void;
  defaultItems?: T[];
  dependencies?: Array<any>;
  resetDeps?: Array<any>;
  debounceTime?: number;
  skipFirst?: boolean;
  initialSearch?: string;
  condition?: boolean;
  resetItemsWhenReset?: boolean;
  uniqueKey?: string;
};

export default function <T>({
  defaultItems = [],
  dependencies = [],
  resetDeps = [],
  debounceTime = null,
  skipFirst = false,
  condition = true,
  initialSearch,
  resetItemsWhenReset = true,
  uniqueKey = '_id',
  customSetItems,
  makeRequest,
  mappingFunction,
  onItemsLoad,
  onResetDeps,
}: InfiniteProps<T>) {
  const [loading, setLoading] = useState(!skipFirst);
  const [items, setItems] = useState<T[]>(defaultItems);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(1);

  const first = useRef(true);
  const resetDataFlag = useRef(false);
  const skipFirstRef = useRef(skipFirst);
  const [searchString, setSearchString] = useState(initialSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(searchString);
  const lastSearchString = useRef<string>(null);

  const shouldContinuePagination = useMemo(
    () => currentPage < totalPages,
    [currentPage, totalPages],
  );

  const { popServerError } = useContext(PopNotificationsContext);

  const onContainerScrolled = useCallback(() => {
    if (loading) return;
    if (currentPage < totalPages) setCurrentPage((old) => old + 1);
  }, [loading, currentPage, totalPages]);

  const setItemsFinal = useMemo(
    () => customSetItems || setItems,
    [customSetItems],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getFiles = useCallback(
    async (
      currentPage: number,
      searchString: string,
      setItemsFinal: React.Dispatch<React.SetStateAction<T[]>>,
    ) => {
      try {
        setLoading(true);

        if (lastSearchString.current !== searchString) {
          setItemsFinal([]);
          lastSearchString.current = searchString;
        }

        const { items, totalPages, totalItems } = await makeRequest(
          currentPage,
          searchString,
        );

        let finalItems = items;

        if (mappingFunction) {
          finalItems = mappingFunction(items);
        }

        if (resetDataFlag.current) {
          setItemsFinal(finalItems);
          resetDataFlag.current = false;
        } else {
          setItemsFinal((old: T[]) =>
            uniqBy([...old, ...finalItems], uniqueKey),
          );
        }

        setTotalItems(totalItems);
        setTotalPages(totalPages);
        onItemsLoad?.(currentPage, finalItems);
      } catch (e) {
        popServerError(e);
      } finally {
        setLoading(false);
      }
    },
    [debounceTime, ...dependencies],
  );

  useEffect(() => {
    if (loading) return;
    if (resetItemsWhenReset) setItems([]);

    onResetDeps?.();
    setCurrentPage(1);
    resetDataFlag.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...resetDeps]);

  const onStringChange = useCallback(
    utils.debounce((searchString) => {
      setCurrentPage(1);
      setDebouncedSearch(searchString);
    }, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    setLoading(true);
    onStringChange(searchString);
  }, [searchString]);

  const callbacks = useRef<Array<() => void>>([]);
  const [callCallbacks, setCallCallbacks] = useState(false);

  useEffect(() => {
    if (skipFirstRef.current && first.current) {
      first.current = false;
      return;
    }
    if (!condition) return;

    callbacks.current.push(async () => {
      await getFiles(currentPage, debouncedSearch, setItemsFinal);
    });
    setCallCallbacks(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentPage, setItemsFinal, condition, ...dependencies]);

  useEffect(() => {
    if (!callCallbacks) return;
    (async () => {
      let i = 0;
      while (callbacks.current.length > i) {
        if (callbacks.current.length - 1 > i) {
          i = callbacks.current.length - 1;
          await callbacks.current[i]();
        } else {
          await callbacks.current[i]();
        }
        i++;
      }
      callbacks.current = [];
      setCallCallbacks(false);
    })();
  }, [callCallbacks]);

  return {
    items,
    loading,
    searchString,
    currentPage,
    totalPages,
    totalItems,
    shouldContinuePagination,
    setItems,
    onContainerScrolled,
    setSearchString,
    setCurrentPage,
  };
}
